import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { arrayMove } from '@dnd-kit/sortable'

import { useFetch } from '@reveni-io/reveni-ui'

import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import { getReturnReasonsPayload } from '@/configuration/pages/ConfigurationPage/ReturnReasonsConfiguration/helpers'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

const useReturnReasonsConfiguration = ({ setToastInfo, returnPortalId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    loading: gettingReasons,
    data: reasons,
    fetchData: getReasons,
  } = useFetch(() => configurationServices.getPortalReturnReasons({ returnPortalId }), {
    doInitialRequest: true,
  })

  const { fetchData: editReturnReasons, loading: editingReturnReasons } = useFetch(
    configurationServices.editPortalReturnReasons,
    {
      doInitialRequest: false,
    }
  )

  const { fetchData: deleteReturnReason } = useFetch(configurationServices.deletePortalReturnReason, {
    doInitialRequest: false,
  })

  const { returnPortalFeatures, fetchingReturnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId,
  })

  const [reasonsSortable, setReasonsSortable] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [reasonSelected, setReasonSelected] = useState(null)
  const [showDeleteReturnReasonModal, setShowDeleteReturnReasonModal] = useState(false)

  const setInitValues = useCallback(() => {
    if (reasons) {
      setReasonsSortable(reasons)
    }

    setHasChanges(false)
  }, [reasons])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const handleDelete = async () => {
    try {
      await deleteReturnReason({ returnPortalId, returnReasonId: reasonSelected.id })
      setToastInfo({ show: true, message: t('Motivo de devolución eliminado'), type: 'success' })
      getReasons()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }

    setShowDeleteReturnReasonModal(false)
  }

  const handleOnOpenDeleteDeliveryMethodModal = reason => {
    setShowDeleteReturnReasonModal(true)
    setReasonSelected(reason)
  }

  const handleCloseDeleteDeliveryMethodModal = () => {
    setShowDeleteReturnReasonModal(false)
  }

  const onAddNewReturnReason = () => {
    navigate(`${routes.configurationPage.returnReasons}/new`)
  }

  const onSave = async () => {
    const returnReasonsPayload = getReturnReasonsPayload(reasonsSortable)

    try {
      await editReturnReasons({ returnPortalId, returnReasons: returnReasonsPayload })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      getReasons()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const reorderReasons = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setReasonsSortable(prev => {
      const oldIndex = prev.findIndex(r => r.id === active.id)
      const newIndex = prev.findIndex(r => r.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
    setHasChanges(true)
  }

  return {
    gettingReasons,
    reasons: reasonsSortable,
    setReasons: param => {
      setReasonsSortable(param)
      setHasChanges(true)
    },
    reorderReasons,
    editingReturnReasons,
    returnPortalFeatures,
    fetchingReturnPortalFeatures,
    hasChanges,
    revertChanges: setInitValues,
    handleDelete,
    showDeleteReturnReasonModal,
    handleOnOpenDeleteDeliveryMethodModal,
    handleCloseDeleteDeliveryMethodModal,
    onAddNewReturnReason,
    onSave,
  }
}

export default useReturnReasonsConfiguration
